/**
 * Diccionario INGLÉS del portal.
 *
 * Terminología de vóley de scouting (no traducción literal): sideout =
 * side-out, armador = setter, recepción = reception, saque = serve, armado =
 * set, defensa = dig, punta = outside hitter, opuesto = opposite, central =
 * middle blocker, líbero = libero, rotación = rotation.
 */
import type { Messages } from './es';

export const en: Messages = {
  nav: {
    brand: 'Scout Portal',
    federation: 'Federation',
    matches: 'Matches',
    competitions: 'Competitions',
    season: 'Season',
    planner: 'Rotations',
    teams: 'Teams',
    settings: 'Settings',
    sameOrigin: 'same origin',
    connecting: 'Connecting…',
    connected: 'API connected',
    connectedTitle: 'API v{version}',
    missingToken: 'Token missing',
    missingTokenTitle: 'The API requires a Bearer token',
    down: 'API not responding',
  },

  common: {
    loading: 'Loading {what}…',
    loadingData: 'data',
    retry: 'Retry',
    refresh: 'Refresh',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    cancel: 'Cancel',
    all: 'All',
    sets: 'Sets',
    set: 'Set {set}',
    total: 'Total',
    player: 'Player',
    team: 'Team',
    unexpectedError: 'Something unexpected went wrong.',
    noData: 'No data',
  },

  routes: {
    unknownBefore: 'That address does not exist. ',
    unknownLink: 'Go to Matches',
    unknownAfter: '.',
    unknownTab: 'That tab does not exist.',
  },

  access: {
    noEvents: 'Your role cannot see the play-by-play detail of this match.',
    noEventsHint:
      'You can still see the summary, the scoresheet and the statistical report. The views built from the raw log (rotations, setter, zones and progression) need the «{permission}» permission: ask the federation for it.',
    noEventsTab: 'This view is built from the play-by-play log, which your role cannot read.',
    noEventsLive:
      'The live score keeps updating, but the play-by-play detail does not: your role cannot read the log.',
    noScoreboard: 'The set-by-set score is rebuilt from the log, which your role cannot read.',
    goToSummary: 'See the summary',
  },

  matches: {
    title: 'Matches',
    searchPlaceholder: 'Search by name or id…',
    searchLabel: 'Search matches',
    loadingWhat: 'matches',
    empty:
      'There are no matches in the archive yet. They show up here as soon as a scouting workstation syncs.',
    noMatchesFor: 'No match matches «{query}».',
    colMatch: 'Match',
    colRules: 'Rulebook',
    colEvents: 'Events',
    colLastSeq: 'Last seq',
    colUpdated: 'Updated',
    colActions: 'Actions',
    scoresheet: 'Scoresheet',
    scoresheetTitle: 'Official match scoresheet, ready to print and sign.',
    report: 'Report',
    reportTitle: 'Statistical report: skills, side-out/break and players.',
    dvw: '.dvw',
    mesa: 'Control desk',
    mesaTitle: 'Scoresheet control desk: officials, challenge and signatures.',
  },

  /** Active efficiency convention (chosen in Settings, shown on every screen). */
  efficiency: {
    chip: 'Convention: {name}',
    chipTitle:
      'Which formulas compute efficiency and positivity on ALL statistics tabs. Change it in Settings.',
  },

  /** "One Rally Before" filter: condition on the previous rally. */
  prevRally: {
    filterLabel: 'Previous rally',
    none: 'No condition',
    teamLabel: 'seen from',
    activeHint:
      'Only rallies whose PREVIOUS rally (same set) matches the condition are counted. Applies to Summary, Setter, Zones and Clips; Rotations and Evolution ignore this cut, and so do the printable documents.',
    preset_afterOwnPoint: 'after an own point',
    preset_afterRivalPoint: 'after an opponent point',
    preset_afterOwnBreak: 'after an own break point',
    preset_afterRivalBreak: 'after an opponent break point',
    preset_afterOwnReceptionError: 'after an own reception error',
    preset_afterOwnAttackError: 'after an own attack error',
    preset_afterOwnAce: 'after an own ace',
  },

  match: {
    loadingWhat: 'the match',
    loadingReport: 'the report',
    printable: 'Printable scoresheet',
    report: 'Statistical report',
    downloadDvw: 'Download .dvw',
    noClosedSets: 'No sets finished yet.',
    tabSummary: 'Summary',
    tabRotations: 'Rotations',
    tabSetter: 'Setter',
    tabZones: 'Zones',
    tabClips: 'Clips',
    tabEvolution: 'Progression',
    tabLive: 'Live',
  },

  summary: {
    noPoints: 'No points recorded.',
    pointBy: 'Point off',
    count: 'Count',
    percent: '%',
    bySkill: 'By skill',
    pointsByReason: 'Points by reason',
    players: 'Players',
    sideout: 'Side-out {pct}',
    sideoutTitle: 'Points won in reception / rallies received',
    breakPoint: 'Break {pct}',
    breakTitle: 'Points won serving / rallies served',
    pointsAttribution: 'Point attribution',
    pointsOrigin: 'Where it came from',
    pointsMeans: 'What it means',
    pointsScoreboard: 'Points on the scoreboard',
    pointsScoreboardHint: 'What the scoreboard says for the selected slice.',
    pointsByPlayer: 'Own action, player coded',
    pointsByPlayerHint: 'Ace, kill or block point with the author coded.',
    pointsUnassigned: 'Own action, no player',
    pointsUnassignedHint: 'The point is the team’s but the log does not say whose.',
    pointsUnassignedNote:
      'The total row adds {count} own-action point(s) with no shirt number, which appear in no player row.',
    pointsOpponentErrors: 'Opponent errors',
    pointsOpponentErrorsHint: 'The opponent gave them away: no own author, and none is invented.',
    pointsIdentity:
      'It adds up: {byPlayer} + {unassigned} + {opponentErrors} = {total} points on the scoreboard.',
    pointsMismatch:
      'The breakdown does not add up to the scoreboard. That is a bug in the report, not in the match: flag it before using these numbers.',
    pointsByPlayerTitle: 'Points by player',
    noScorers: 'No player scored points from an own action in the selected slice.',
    noPointsBreakdown:
      'This API does not publish the point attribution breakdown (it is an older version of the report).',
    player: 'Player',
    ace: 'Ace',
    aceTitle: 'Serve points',
    kill: 'Attack',
    killTitle: 'Attack points',
    block: 'Block',
    blockTitle: 'Block points',
    pointsChip: '{total} pts · {byPlayer} with author',
    pointsChipTitle: 'Points on the scoreboard and how many have a player coded',
  },

  skills: {
    empty: 'No skills coded.',
    noPlayers: 'No players with actions.',
    skill: 'Skill',
    total: 'Tot',
    perfect: '#',
    perfectTitle: 'Perfect',
    errors: '=',
    errorsTitle: 'Errors',
    efficiency: 'Eff%',
    efficiencyTitle: 'Efficiency',
    positivity: 'Pos%',
    positivityTitle: 'Perfect / positivity in reception',
    matchesPlayed: 'MP',
    points: 'Pts',
    pointsTitle: 'Points scored',
    names: {
      serve: 'Serve',
      reception: 'Reception',
      set: 'Set',
      attack: 'Attack',
      block: 'Block',
      dig: 'Dig',
      freeball: 'Free ball',
    },
    reasons: {
      attack: 'Attack',
      block: 'Block',
      serve: 'Serve',
      opponent_error: 'Opponent error',
      sanction: 'Sanction',
      delay_penalty: 'Delay penalty',
      illegal_substitution: 'Illegal substitution',
      position_fault: 'Position fault',
      challenge_adjustment: 'Challenge adjustment',
      forfeit: 'Forfeit',
      other: 'Other',
    },
  },

  rotations: {
    intro:
      'Rotation = setter position (DataVolley convention). Each rally counts in the rotation it was played in; a double substitution keeps the setter slot.',
    noLineup:
      'The log has no starting lineup (lineup_set event), so there is no way to tell which rotation each rally was played in.',
    noSetter:
      'There is a lineup, but the setter cannot be identified (no setterPosition in the lineup and no player on the roster is listed as a setter).',
    untracked: 'Rotation cannot be reconstructed.',
    unassignedSuffix: ' ({count} rallies not assigned to a rotation)',
    unassignedChip: '{count} unassigned',
    unassignedTitle: 'Rallies played before the lineup was known',
    sideoutByRotation: 'Side-out by rotation',
    colRotation: 'Rot.',
    colRallies: 'Rallies',
    colReceived: 'Rec',
    colReceivedTitle: 'Rallies received',
    colSideout: 'Side-out%',
    colServed: 'Srv',
    colServedTitle: 'Rallies served',
    colBreak: 'Break%',
    colNet: 'Net',
    colNetTitle: 'Points won − points lost',
    barTitle: '{label}: side-out {pct} ({won}/{total})',
  },

  setter: {
    intro:
      'The target is identified from the attack combination; when it is not coded, the start zone is used and, as a last resort, the end zone.',
    attacksSet: '{count} attacks set',
    empty:
      'No attacks are tied to a coded reception: with no receptions in the log this view stays empty (it is not an error).',
    receptionHeading: 'Reception {grade} ({name}) — {count} attacks',
    gradePerfect: 'perfect',
    gradePositive: 'positive',
    gradeNeutral: 'neutral',
    gradeNegative: 'poor',
    colTarget: 'Target',
    colSource: 'Data source',
    colAttacks: 'Attacks',
    colPercent: '%',
    colEfficiency: 'Eff%',
    uncoded: 'not coded',
    sourceCombo: 'combination',
    sourceStartZone: 'start zone',
    sourceEndZone: 'end zone',
    sourceUnknown: '—',
    segmentTitle: '{target}: {count} attacks ({pct}), efficiency {efficiency}',
    receptionCardTitle: '{team} · reception → point',
    receptionCardHint:
      'Conversion = points won over the rallies WITH a coded reception. This is not the Side-out% shown in the summary and in rotations, which runs over every rally received: that is why the two numbers may differ.',
    noReceptions: 'No receptions coded.',
    colGrade: 'Grade',
    colRallies: 'Rallies',
    colSideout: 'Points',
    colSideoutPct: 'Conversion%',
    noSetAttacks: 'No attacks set off this reception.',
    phaseFilter: 'Phase',
    phaseAll: 'Both phases',
    phaseSideout: 'First ball',
    phaseTransition: 'Transition',
    phaseAllHint:
      'Side-out and transition together: the full setter distribution, conditioned on the reception grade.',
    phaseSideoutHint:
      'First ball: the attack coming straight off the reception, with every option open and the opposing block yet to read it.',
    phaseTransitionHint:
      'Counter-attack rebuilt after an own dig, where usually a single option is left. It includes the rallies the team served.',
    phaseTotal: '{count} attacks in this phase',
    phaseWithReception: '{count} off a settable reception',
    phaseWithReceptionTitle:
      'Attacks from rallies with an own reception graded #, +, ! or −: the only ones that enter the per-grade tables',
    phaseWithoutReception: '{count} with no own reception',
    phaseWithoutReceptionTitle:
      'Attacks from rallies the team served, or with a / or = reception: they exist, but there is no grade to condition on',
    phaseEmpty: 'No attacks in this phase.',
    phaseNoGrades:
      'The attacks in this phase do not come off a coded settable reception, so there is no per-grade table.',
    phaseAllTargets: 'Every target in this phase',
  },

  clips: {
    filterCard: 'Filter',
    intro:
      'Every coded touch with a VAR timecode is a clip. Pick the slice and request the videos: they are cut by the venue replay server, not by the portal.',
    team: 'Team',
    bothTeams: 'Both',
    player: 'Shirt',
    allPlayers: 'All',
    playerNeedsTeam: 'Pick a team to filter by shirt number.',
    skill: 'Skill',
    allSkills: 'All',
    skill_serve: 'Serve',
    skill_reception: 'Reception',
    skill_set: 'Set',
    skill_attack: 'Attack',
    skill_block: 'Block',
    skill_dig: 'Dig',
    skill_freeball: 'Freeball',
    evaluation: 'Grade',
    allEvaluations: 'All',
    rotation: 'Rotation',
    allRotations: 'All',
    rotationOption: 'P{position} (setter)',
    setsHint: 'The set slice is the selector above, the same one every tab uses.',
    camerasCard: 'Cameras',
    camerasWhat: 'the VAR cameras',
    camerasError: 'Could not read the VAR camera list ({reason}).',
    noCameras: 'The VAR declares no cameras.',
    noVar: 'No replay server configured in this browser.',
    noVarHint:
      'The cut list still works (label, timecode and duration): what is missing is who produces the file. Set it up in Settings → Replay server (VAR), with the venue URL.',
    listCard: 'Clips',
    loadingWhat: 'the clips',
    count: '{count} clip(s)',
    skipped: '{count} touch(es) without timecode: no clip possible.',
    files: '{files} file(s) — one cut per each of the {cameras} selected camera(s).',
    requestAll: 'Request the {count} clips',
    jobsSummary: '{done} ready · {working} in progress · {failed} failed',
    emptyForFilter: 'No clips for this filter.',
    colClip: 'Clip',
    colSet: 'Set',
    colTimecode: 'Timecode',
    colDuration: 'Length',
    colState: 'Status',
    seconds: '{seconds} s',
    timecodeTitle: 'videoNs {ns} (VAR master clock)',
    request: 'Request clip',
    download: 'Download',
    failed: 'Failed',
    state_pending: 'Requesting…',
    state_queued: 'Queued',
    state_running: 'Cutting',
  },

  zones: {
    intro:
      '«n/d» groups the touches without that coordinate coded: they are kept, so the total adds up. Colour is the efficiency observed in this very matrix and the exact number is written in each cell.',
    skillFilter: 'Skill',
    skillAttack: 'Attack',
    skillServe: 'Serve',
    skillReception: 'Reception',
    introAttack: 'Rows = attack start zone, columns = end zone.',
    introServe: 'Rows = zone the serve was hit from, columns = zone it was sent to.',
    introReception:
      'Rows = zone the serve came from, columns = zone where it was received. «Perfect» here means positivity (# + +), which is what is read on court.',
    playerFilter: 'Player',
    serverFilter: 'Server',
    receiverFilter: 'Passer',
    wholeTeam: 'Whole team',
    emptyTeam: 'No attacks coded in the selected sets.',
    emptyPlayer: '#{player} has no attacks coded in the selected sets.',
    emptyTeamServe: 'No serves coded in the selected sets.',
    emptyPlayerServe: '#{player} has no serves coded in the selected sets.',
    emptyTeamReception: 'No receptions coded in the selected sets.',
    emptyPlayerReception: '#{player} has no receptions coded in the selected sets.',
    attacks: '{count} attacks',
    serves: '{count} serves',
    receptions: '{count} receptions',
    kills: '{count} kills',
    aces: '{count} aces',
    perfectReceptions: '{count} perfect',
    killPct: 'Kill {pct}',
    acePct: 'Ace {pct}',
    positivityPct: 'Positivity {pct}',
    errors: '{count} errors',
    blocked: '{count} blocked',
    efficiency: 'Efficiency {pct}',
    cellTitle:
      'Start {startZone} → end {endZone}: {total} attacks, {kills} kills, {errors} errors, {blocked} blocked · efficiency {efficiency}',
    cellTitleServe:
      'Serve from {startZone} → zone {endZone}: {total} serves, {kills} aces, {errors} errors, {blocked} returned · efficiency {efficiency}',
    cellTitleReception:
      'Serve from {startZone} → received at {endZone}: {total} receptions, {kills} perfect, {errors} errors, {blocked} overpassed · efficiency {efficiency}',
    noZoneData: 'n/d',
    emptyMatrix: 'No touches with a coded zone.',
    axisRow: 'Start',
    axisCol: 'End',
    axisRowServe: 'Served from',
    axisColServe: 'Sent to',
    axisRowReception: 'Served from',
    axisColReception: 'Received at',
    matrixLabel: '{rowLabel} × {colLabel} matrix',
    legend: 'Efficiency',
  },

  evolution: {
    empty: 'There are no points in the log yet to draw the progression.',
    emptySet: 'Set {set} has no points recorded.',
    chartLabel: 'Progression of set {set}',
    chartTitle: 'Set {set}: {homeName} {home} – {awayName} {away}',
    firstRally: 'rally 1',
    lastRally: 'rally {count}',
    barChartLabel: 'Bar chart',
    distributionLabel: 'Distribution',
  },

  live: {
    statusIdle: 'Not connected',
    statusConnecting: 'Connecting…',
    statusOpen: 'Live',
    statusReconnecting: 'Reconnecting…',
    statusClosed: 'Disconnected',
    statusError: 'Connection error',
    sets: 'Sets {home}–{away}',
    setClosed: 'set finished',
    lastEvents: 'Latest events',
    empty:
      'No event has come in yet. If the match is being played, the scouting workstation has to be syncing against this API.',
    openFailed: 'The API answered {status} when opening the live stream.',
    noFetch: 'This environment has no fetch.',
    noStreamBody: 'The browser does not expose the stream body.',
  },

  /** Match console (Phase D). Read from a metre away: short, one idea each. */
  bench: {
    views: {
      banco: 'Bench',
      mesa: 'Scorer',
      arbitro: 'Referee',
      espejo: 'Supervisor',
      tablero: 'Scoreboard',
    },
    access: {
      title: 'Match console',
      explain: 'Scan the QR the scorer’s table shows to enter this match.',
      hint: 'The credential is valid for that match only, with the role you were given.',
      loading: 'Loading the match…',
      failed: 'The credential could not be redeemed. Ask the table to issue a new one.',
    },
    link: {
      offline: 'NO NETWORK',
      cold: 'NO DATA',
      keepPlaying: 'The match goes on. Without a tablet, the official request is the hand signal.',
      lastData: 'last update {seconds} s ago',
      retry: 'Retry',
    },
    prefs: {
      glareOn: 'High contrast',
      glareOff: 'Normal contrast',
    },
    score: {
      serving: 'Serving',
      servingShort: 'SRV',
      closed: 'SHEET CLOSED',
      timeoutsLeft: 'time-outs',
      subsLeft: 'subs',
      challengesLeft: 'challenges',
      timeoutsLeft2: '{n} left',
      challengesLeft2: '{n} left',
      exceptional: '{count} exceptional',
    },
    court: {
      title: 'Court · {team}',
      flip: 'Rotate court',
      unflip: 'Back',
      noLineup: 'No line-up recorded for this set yet.',
      net: 'NET',
    },
    rallies: {
      title: 'Set {set} rallies',
      empty: 'No point played yet.',
      servedBy: 'served by {team}',
      unknownServer: 'server not recorded',
      jump: 'Play {tc}',
      noTimecode: 'no video',
    },
    numbers: {
      title: 'Match numbers',
      loading: 'Computing…',
      unavailable: 'Numbers are not available with this credential.',
      sideout: 'Side-out',
      break: 'Break',
      attack: 'Attack',
      reception: 'Reception +',
      topScorers: 'Points',
    },
    request: {
      title: 'Request',
      timeout: 'Time-out',
      challenge: 'Challenge',
      substitution: 'Substitution',
      sending: 'Sending…',
      noNetwork: 'Could not send',
      useSignal: 'Ask the 2nd referee with the hand signal: with no network, the official request is the physical one.',
      underReview: 'A review is under way: no time-outs or substitutions.',
      subsUsed: '{used} of {limit}',
      subsWarn: 'Few substitutions left in this set.',
      noSubsLeft:
        'No substitutions left in this set. The exceptional substitution (rule 15.7) is requested by hand signal and authorised by the 2nd referee.',
      whoLeaves: 'Who goes out?',
      whoEnters: 'Who comes in?',
      cannotLeave: 'Already went out and came back in this set.',
      pairLocked: 'Only #{player} may come in: they are the one who replaced them (rule 15.6).',
      noCandidates: 'Nobody available on the bench.',
    },
    lineup: {
      title: 'Set {set} line-up',
      official: 'A line-up sent from the tablet is official (rule 5.2.3.1).',
      send: 'Send line-up',
      clear: 'Clear',
      useSheet: 'No network: hand in the paper line-up sheet.',
    },
    queue: {
      title: 'Requests',
      empty: 'No requests.',
      pending: '{n} waiting',
      ago: '{seconds} s ago',
      by: 'requested by {who}',
      accept: 'Accept',
      reject: 'Reject',
      cancel: 'Withdraw',
      accepted: 'Accepted by {who}',
      rejected: 'Rejected: {reason}',
      cancelled: 'Withdrawn by the bench.',
      stale: 'Expired: play moved on. Record it by hand if it applies.',
      subDetail: '#{out} out, #{in} in',
      kinds: {
        timeout: 'Time-out',
        substitution: 'Substitution',
        challenge: 'Challenge',
        lineup: 'Line-up',
      },
    },
  },

  mesa: {
    title: 'Control desk',
    tabs: {
      oficiales: 'Officials',
      challenge: 'Challenge',
      acta: 'Score sheet',
    },
    loadingWhat: 'the score sheet',
    closedBanner:
      'The score sheet is closed and signed: anything arriving now needs an authorised rectification.',
    phase: {
      BEFORE_MATCH: 'Before the match',
      AFTER_TOSS: 'Toss done',
      DURING_MATCH: 'Match in progress',
      AFTER_MATCH: 'Match finished',
      CLOSED: 'Sheet closed',
    },
    team: {
      home: 'home',
      away: 'away',
    },
    roles: {
      referee1: '1st referee',
      referee2: '2nd referee',
      challenge_referee: 'Challenge referee',
      reserve_referee: 'Reserve referee',
      scorer: 'Scorer',
      assistant_scorer: 'Assistant scorer',
      line_judge: 'Line judge',
      team_delegate: 'Bench delegate',
      team_captain: 'Captain',
      supervisor: 'Supervisor',
      replay_operator: 'Replay operator',
      scoreboard: 'Stadium scoreboard',
      statistician: 'Statistician',
      system: 'System',
    },
    oficiales: {
      title: 'Match officials',
      explain:
        'What you enter here goes on the sheet (APPROVAL box) and defines who can sign what.',
      empty: 'No officiating crew loaded yet.',
      role: 'Role',
      name: 'Name',
      country: 'Country (ISO3)',
      license: 'Licence',
      add: 'Add official',
      remove: 'Remove',
      save: 'Record on the sheet',
      saved: 'Officiating crew recorded on the sheet.',
      nameMissing: 'Every official needs a name.',
      credsTitle: 'Match credentials (QR+PIN)',
      credsExplain:
        'One per person present. The link opens the console with their role; the PIN is read out to the holder and is what signs: the link without the PIN operates, it does not sign.',
      credsEmpty: 'No credential issued yet.',
      holder: 'Holder',
      side: 'Bench',
      sideNone: 'No bench',
      issue: 'Issue credential',
      holderMissing: 'The holder needs a name.',
      revoke: 'Revoke',
      revoked: 'Revoked',
      confirmRevoke: 'Revoke {holder}’s credential? It stops authenticating immediately.',
      kindAccount: 'registered account',
      kindCredential: 'QR+PIN',
      issuedTitle: 'Credential issued — this is shown ONCE',
      issuedLink: 'Access link (this is the QR content)',
      issuedPin: 'PIN — read it out to the holder, do not leave it on display',
      copyLink: 'Copy link',
      copied: 'Copied.',
      qrPending:
        'Have the holder scan it with their tablet camera: it opens the console straight away with their role. Without a camera, the link can also be copied or typed.',
      issuedDone: 'Done, handed over',
    },
    challenge: {
      title: 'Challenge in progress',
      none: 'No review in progress.',
      windowOpen: 'The window for the last point is open: it can still be challenged (art. 7).',
      remainingNow: 'Challenges left — {home} home · {away} away',
      requestedBy: 'Challenged by the {team} team',
      requestedByRef: 'Review initiated by the 1st referee (art. 12)',
      status: {
        AWAITING_REASON: 'Awaiting the reason',
        UNDER_REVIEW: 'Under review',
      },
      setScore: 'Set {set} · {score} (requesting team first)',
      at: 'Requested at {tc} of the video',
      claimed: 'Claimed fault: {fault}',
      claimedNone: 'No fault declared yet (art. 8: chosen in a second step)',
      pickFault: 'Record the fault the bench chose',
      faults: {
        BALL_IN_OUT: 'Ball in/out',
        BLOCK_TOUCH: 'Block touch',
        NET_FAULT: 'Net fault',
        ANTENNA_TOUCH: 'Antenna touch',
        FOOT_FAULT: 'Foot fault',
        FLOOR_TOUCH: 'Floor touch',
        LAST_TOUCH: 'Last touch',
        REACH_OVER: 'Reach over the net',
      },
      resolveTitle: 'Review outcome',
      onlyUnsuccessful:
        'The request never declared a fault: the only possible resolution is “unsuccessful” (art. 24).',
      outcomes: {
        SUCCESSFUL: 'Successful',
        UNSUCCESSFUL: 'Unsuccessful',
        INCONCLUSIVE: 'Inconclusive',
        DECLINED_UNNECESSARY: 'Unnecessary',
        FOOTAGE_UNAVAILABLE: 'No footage',
      },
      outcomeHints: {
        SUCCESSFUL: 'The claim was right. The challenge is retained (art. 2).',
        UNSUCCESSFUL: 'The claim was wrong. The challenge is LOST (art. 11).',
        INCONCLUSIVE: 'The footage cannot decide it. The challenge is retained (art. 2).',
        DECLINED_UNNECESSARY: 'It fell on its own: rally won anyway or the opponent admitted (art. 14). Retained.',
        FOOTAGE_UNAVAILABLE: 'There was no footage to review. The challenge is retained.',
      },
      retained: 'The team RETAINS the challenge.',
      lost: 'The team LOSES the challenge.',
      observed: 'Observed fault (the first fault observed prevails, even if not the one challenged — art. 13)',
      report: 'Challenge referee report (optional)',
      scoreTitle: 'Score reconciliation',
      scoreExplain:
        'This is not a generic undo: INVERT changes the serving team and DELETE gives the serve back to the same server. A mistake here breaks the rotation for the rest of the set.',
      actions: {
        CONFIRM: 'CONFIRM',
        INVERT: 'INVERT the point',
        DELETE_LAST_POINT: 'DELETE the last point',
        REPLAY: 'REPLAY the rally',
      },
      actionHints: {
        CONFIRM: 'The score stays as it is.',
        INVERT: 'The point goes to the other team: the serve changes hands.',
        DELETE_LAST_POINT: 'The point is deleted: the serve goes back to the same server.',
        REPLAY: 'The point is deleted and the rally is replayed: the same team serves.',
      },
      planConfirm: 'The score does not change.',
      planInvert: 'The last point is inverted: it goes to the {team} team.',
      planDelete: 'The last point is deleted: the serve goes back to the same server.',
      planReplay: 'The point is deleted and the rally is replayed.',
      afterRemaining: 'After this: {home} left for home · {away} for away.',
      noPoint: 'The log has no standing point to reconcile.',
      confirm: 'Record the resolution',
      resolvedOk:
        'Challenge resolved and score reconciled in a single batch. 2nd referee checkpoint: sheet, score and rotation before authorising the serve.',
      reviewSeconds: 'Review in progress: {seconds} s (no regulatory time limit)',
      logTitle: 'Challenges of the match',
      logEmpty: 'None yet.',
      logRetained: 'retained',
      logLost: 'lost',
    },
    acta: {
      title: 'Signatures and closure',
      rosterTitle: 'Roster verification (rule 4.1.3)',
      rosterExplain:
        'With each bench’s pre-match signatures, that team’s roster is frozen: after this nobody else joins the list.',
      rosterLocked: 'Roster FROZEN',
      rosterOpen: 'Roster still open',
      players: '{count} players listed',
      signaturesTitle: 'Closing signatures — instruction 4.10, in this order',
      scorerFirst:
        'The scorer signs first: their signature freezes the digest and everyone after signs THAT document (rule 27.2.3.3).',
      signedBy: 'Signed by {name}',
      pendingSlot: 'Signature pending',
      nextBadge: 'NEXT TO SIGN',
      noHolder: 'Not accredited: issue their credential in Officials.',
      digestMismatch: 'This signature does NOT match the frozen digest! The sheet changed between signatures.',
      sign: 'Sign',
      signAs: '{name} signs ({role})',
      secretPin: 'Their credential PIN — entered by the holder, not the desk',
      secretPassword: 'Their account password — entered by the holder, not the desk',
      needsToken:
        'This credential was issued in another browser and the desk does not hold its token: re-issue it in Officials or have them sign from their own device.',
      accountHint:
        'Registered account: signs with their password. Valid if this desk’s active session is theirs, or from their own session.',
      signedOk: 'Signature recorded and bound to the document digest.',
      frozenDigest: 'Frozen digest: {digest}',
      closeTitle: 'Closure and delivery',
      closeExplain:
        'Closing freezes the document that was signed: after this the engine rejects any addition without an authorised rectification (§8.4).',
      outcomeLabel: 'Match outcome',
      outcomes: {
        NORMAL: 'Normal',
        DEFAULT: 'Forfeit (0-3, rule 6.4)',
        INCOMPLETE: 'Incomplete team (keeps its points)',
        WITHDRAWN: 'Withdrawn',
      },
      deliveriesLabel: 'Delivery of the signed document',
      deliveries: {
        FEDERATION: 'Federation',
        TEAM_A: 'Team A',
        TEAM_B: 'Team B',
        ORGANISER: 'Organising committee',
        SUPERVISOR: 'Supervisor',
      },
      close: 'CLOSE THE SHEET',
      confirmClose:
        'Close the sheet? It freezes the signed document: afterwards it is only touched with an authorised rectification.',
      closeUnsigned: 'Without any closing signature it cannot be closed: the scorer signs first.',
      closedOk: 'Sheet closed and delivered.',
      closedDigest: 'Digest of the closed document: {digest}',
      printable: 'Open the score sheet',
    },
  },

  season: {
    title: 'Season',
    includedMatches: 'Matches included',
    apply: 'Apply',
    noMatches: 'There are no matches in the archive yet.',
    loadingWhat: 'matches',
    loadingTotals: 'the totals',
    allSeason: 'No selection = the whole season.',
    selectedCount: '{count} match(es) selected.',
    totals: 'Totals · {count} match(es)',
    setsChip: 'Sets: {sets}',
    setsAll: 'all',
    colTeam: 'Team',
    colMatches: 'MP',
    colSideout: 'Side-out%',
    colBreak: 'Break%',
    colReceived: 'Rec',
    colReceivedTitle: 'Rallies received',
    colServed: 'Srv',
    colServedTitle: 'Rallies served',
    topEfficiency: 'Top attack efficiency',
    minAttacks: 'At least {count} attacks in the totals.',
    topPoints: 'Top scorers',
    unitAttacks: 'attacks',
    unitMatches: 'MP',
    bySkill: 'By skill',
    players: 'Players',
    noRanked: 'No player qualifies yet.',
    colValue: 'Value',
  },

  /** Rotation Planner (#/planner): the rotation whiteboard. */
  planner: {
    title: 'Rotation Planner',
    intro:
      'Starting six → the 6 rotations: who ends up in each zone, which overlaps the rules demand, and how each rotation performed when there is data.',
    sourceCard: 'Match data (optional)',
    sourceIntro:
      'Pick a match to preload the starting six, put names on the shirt numbers and cross each rotation with its real statistics.',
    matchLabel: 'Match',
    noMatch: 'No match (blank board)',
    teamLabel: 'Team',
    prefill: 'Load starting six',
    seasonToggle: "Accumulate the team's whole season",
    noEvents:
      'Your role cannot read the event log, so there is no preload and no per-rotation statistics.',
    noEventsHint: 'The board still works: type the shirt numbers by hand.',
    loadingSeason: "the team's season",
    seasonEmpty:
      'No match of the season has a reconstructable rotation for this team (missing line-up or no identifiable setter).',
    lineupCard: 'Starting six',
    lineupIntro:
      'One shirt number per position. P1 is the right back (the server); P2–P3–P4 are the net.',
    slotLabel: 'Shirt at P{position}',
    setterLabel: 'Setter',
    noSetter: 'No setter',
    duplicates: 'Repeated shirt numbers: {numbers}. No rotation is legal like this.',
    incomplete: 'The six is incomplete: rotations are drawn anyway, with gaps.',
    noSetterHint:
      'Without a chosen setter the rotations are not numbered (P1..P6 is the setter position) and cannot be crossed with statistics.',
    empty: 'Type the six (or preload it from a match) and the 6 rotations appear here.',
    untracked:
      'This team has no reconstructable rotation in the chosen match (no line-up or no identifiable setter): cards show no statistics.',
    rotationTitle: 'Rotation {order}',
    rotationTitleSetter: 'Rotation {order} — setter at P{position}',
    ralliesTitle: 'Rallies played in this rotation within the chosen data.',
    rallies: '{count} rallies',
    sideoutTitle: 'Points won receiving / rallies received, in this rotation.',
    sideout: 'SO {pct}',
    breakTitle: 'Points won serving / rallies served, in this rotation.',
    breakPoint: 'BP {pct}',
    netTitle: 'Points won minus lost in this rotation.',
    setterChip: 'S',
    overlapsTitle: 'Overlaps to watch (rule 7.4)',
    overlapFrontBack:
      '{back} (Z{backZone}) must stay behind {front} (Z{frontZone}) until the serve.',
    overlapLeftRight:
      '{left} (Z{leftZone}) must stay to the left of {right} (Z{rightZone}) until the serve.',
    emptySlot: 'gap',
  },

  competition: {
    // --- Listado -------------------------------------------------------------
    title: 'Competitions',
    subtitle: 'Tournaments, fixtures and standings of the federation.',
    loadingWhat: 'competitions',
    empty:
      'No competitions yet. A competition groups phases (regular season, groups, playoff), its fixture and its standings — and links the matches already in the archive.',
    colName: 'Competition',
    colSeason: 'Season',
    colStatus: 'Status',
    colTeams: 'Teams',
    colPhases: 'Phases',
    colUpdated: 'Updated',
    open: 'Open',
    statusDraft: 'Draft',
    statusActive: 'Running',
    statusFinished: 'Finished',

    // --- Ficha ---------------------------------------------------------------
    tabFixture: 'Fixture',
    tabStandings: 'Standings',
    tabInfo: 'Details',
    notFound: 'That competition does not exist.',
    backToList: 'Back to competitions',
    loadingCompetition: 'the competition',
    phase: 'Phase',
    group: 'Group',
    allPhases: 'All phases',
    teamsTitle: 'Participating teams',
    phasesTitle: 'Phases',
    phaseKindRegular: 'Round robin',
    phaseKindGroups: 'Groups',
    phaseKindPlayoff: 'Knockout',
    scoringTitle: 'Points system',
    scoringIntro: 'Every competition awards points its own way; the system is data, not hardcoded.',
    scoringRule: '{winner}-{loser} → {winnerPoints} and {loserPoints}',
    scoringFallback: 'Any other result: {winnerPoints} for the winner and {loserPoints} for the loser.',
    scoringForfeit: 'Walkover: {sets} with {points} per set.',
    tiebreakTitle: 'Tiebreakers, in order',
    legs: 'Legs',
    legsOne: 'Single round',
    legsTwo: 'Home and away',

    // --- Fixture -------------------------------------------------------------
    matchday: 'Matchday {matchday}',
    fixtureEmpty:
      'This competition has no fixture yet. You can generate it from a phase or add matches by hand.',
    generate: 'Generate fixture',
    generating: 'Generating…',
    generateHint:
      'Generates the calendar for the chosen phase. Regenerating does NOT unlink matches already played.',
    generateDone: 'Fixture generated: {count} match(es).',
    thirdPlace: 'With third place match',
    vs: 'vs',
    noRival: 'To be decided',
    winnerOf: 'Winner of {of}',
    loserOf: 'Loser of {of}',
    roundFinal: 'Final',
    roundSemi: 'Semifinal',
    roundQuarter: 'Quarterfinals',
    roundOf16: 'Round of 16',
    roundNumber: 'Round {round}',
    matchNumber: '{round} {position}',
    thirdPlaceMatch: 'Third place',
    statusScheduled: 'Scheduled',
    statusLive: 'Live',
    statusPlayed: 'Played',
    statusWalkover: 'Walkover',
    statusPostponed: 'Postponed',
    statusCancelled: 'Cancelled',
    originLog: 'from scouting',
    originManual: 'entered by hand',
    originWalkover: 'by regulation',
    resultFrom: 'Result {origin}',
    openMatch: 'Open the match',
    venue: 'Venue',
    colDate: 'Date',
    colMatch: 'Match',
    colResult: 'Result',
    colSets: 'Set scores',
    colStatusFixture: 'Status',

    // --- Posiciones ----------------------------------------------------------
    standingsEmpty: 'This competition has no phase with a table yet.',
    standingsIntro:
      'The table is computed from the matches on file: it is never uploaded by hand. Only played matches and walkovers count.',
    computedWith: 'Computed with {scoring}',
    matchesCounted: '{counted} of {total} match(es) with a result',
    colPos: '#',
    colTeam: 'Team',
    colPlayed: 'P',
    colWon: 'W',
    colLost: 'L',
    colSetsWon: 'SW',
    colSetsLost: 'SL',
    colSetRatio: 'Set ratio',
    colPointsFor: 'PF',
    colPointsAgainst: 'PA',
    colPointRatio: 'Point ratio',
    colTablePoints: 'Pts',
    colAdjustment: 'Adj.',
    tied: 'Tied with the team above',
    brokenBy: 'Split by: {criterion}',
    adjustmentTitle: 'Points adjustment: {points} ({reason})',
    tbPoints: 'points',
    tbMatchesWon: 'matches won',
    tbSetRatio: 'set ratio',
    tbSetDiff: 'set difference',
    tbPointRatio: 'point ratio',
    tbPointDiff: 'point difference',
    tbHeadToHead: 'head-to-head',
    tbSetsWon: 'sets won',
    tbPointsScored: 'points scored',

    // --- Enganche de partidos ------------------------------------------------
    linkTitle: 'Link matches from the archive',
    linkHint:
      'Matches already on file that do not belong to any matchday. Once linked, the result comes from the log: the table updates itself.',
    linkEmpty: 'No loose matches left to link.',
    linkAction: 'Link {count} match(es)',
    linkFilter: 'Filter by the competition declared in the match',
    linkDone: '{linked} match(es) linked; {teams} team(s) created.',
    linkSkipped: '{count} match(es) could not be linked.',
    selectAll: 'Select all',
    clearSelection: 'Clear selection',
  },

  competitionForm: {
    newCompetition: 'New competition',
    edit: 'Edit',
    remove: 'Delete',
    editing: 'Editing {id}',
    savedNotice: 'Competition “{name}” saved.',
    deletedNotice: 'Competition “{name}” deleted.',
    confirmDelete:
      'Delete competition “{name}”? Its fixture goes with it; matches already recorded stay in the archive.',
    noPermission:
      'Creating or editing competitions requires the {permission} permission, which today belongs to the administrator role.',

    identityTitle: 'Competition details',
    competitionId: 'Id',
    competitionIdHint: 'Used in the URL and links the fixture. It cannot be changed later.',
    competitionIdPlaceholder: 'liga-a1-2026',
    name: 'Name',
    namePlaceholder: 'Liga A1',
    season: 'Season',
    seasonPlaceholder: '2026 or 2025/26',
    status: 'Status',
    sportConfig: 'Rulebook',
    description: 'Description',

    teamsTitle: 'Participating teams',
    teamsHint:
      'One club per line: “id, Name, seed”. With a single column it is taken as the name and the id is proposed automatically. The seed defines the knockout bracket.',
    teamsPlaceholder: 'bolivar, Club Bolívar, 1\nupcn, UPCN Vóley, 2\nCiudad Vóley',
    teamsCount: '{count} team(s) loaded.',

    scoringTitle: 'Points system',
    scoringHint:
      'How each result awards points. It is competition data, not hardcoded: if this league gives 4 points for a 3-1, that is one more line.',
    preset: 'Preset',
    presetCustom: 'Custom',
    presetFivb: 'FIVB (3-0 and 3-1 → 3; 3-2 → 2 and 1)',
    presetFivbBo3: 'Best of 3 (2-0 → 3; 2-1 → 2 and 1)',
    presetWinLoss21: '2 points per win, 1 per loss',
    presetWin10: '1 point per win',
    scoringLabel: 'Label',
    rules: 'Points table',
    rulesHint:
      'One line per result, always from the winner’s perspective: “3-2 2 1” = the team winning 3-2 gets 2 and the loser 1.',
    rulesPlaceholder: '3-0 3 0\n3-1 3 0\n3-2 2 1',
    winPoints: 'Winner points',
    lossPoints: 'Loser points',
    fallbackHint:
      'Used for results the table does not list (a three-set match in a competition declared best of five, for instance).',
    forfeitTitle: 'Walkover / team not present',
    forfeitSets: 'Winner sets',
    forfeitLoserSets: 'Loser sets',
    forfeitPointsPerSet: 'Points per set',
    forfeitWinnerPoints: 'Winner points',
    forfeitWinnerPointsHint: 'Empty = whatever the table says for that result.',
    forfeitLoserPoints: 'Loser points',

    tiebreakTitle: 'Tiebreak criteria',
    tiebreakHint:
      'The ORDER matters as much as the list: two leagues with the same criteria in a different order produce different tables.',
    tiebreakAdd: 'Add criterion',
    tiebreakUp: 'Move up',
    tiebreakDown: 'Move down',
    tiebreakRemove: 'Remove',
    tiebreakRestore: 'Back to the FIVB order',
    tiebreakEmptyHint: 'With no criteria the table cannot order two tied teams.',

    phasesTitle: 'Phases',
    phasesHint:
      'A competition is one or more phases: regular, groups or knockout. The fixture is generated per phase, from the competition page.',
    phasesEmpty: 'No phases yet. Without at least one, no fixture can be generated.',
    addPhase: 'Add phase',
    removePhase: 'Remove phase',
    phaseNumber: 'Phase {number}',
    phaseId: 'Phase id',
    phaseName: 'Name',
    phaseNamePlaceholder: 'Regular phase',
    phaseKind: 'Type',
    phaseLegs: 'Legs',
    phaseTeams: 'Teams in this phase',
    phaseTeamsHint:
      'Comma-separated ids. Empty = every team in the competition, ordered by seed.',
    phaseGroups: 'Groups',
    phaseGroupsHint: 'One per line: “Group A: bolivar, upcn”. Group phase only.',
    phasePreserved:
      'This phase has settings loaded through the API that this screen does not edit ({detail}). They are kept as they are when saving.',
    phasePreservedScoring: 'its own points system',
    phasePreservedTiebreakers: 'its own tiebreakers',
    phasePreservedAdjustments: '{count} points adjustment(s)',

    errors: {
      idRequired: 'The competition id is missing.',
      idInvalid: 'The id only accepts letters, digits, dot, dash and underscore.',
      nameRequired: 'The competition name is missing.',
      seasonTooLong: 'The season cannot exceed 40 characters.',
      descriptionTooLong: 'The description cannot exceed 1000 characters.',
      number: 'Field “{field}” has an invalid value.',
      teamId: 'Teams line {line}: the id is missing.',
      teamIdInvalid:
        'Teams line {line}: id “{id}” only accepts letters, digits, dot, dash and underscore.',
      teamName: 'Teams line {line}: the club name is missing.',
      teamRepeated: 'Teams line {line}: id “{id}” appears twice.',
      teamSeed: 'Teams line {line}: “{token}” is not a valid seed (1 to 256).',
      ruleShape:
        'Points table line {line}: four numbers are expected (winner sets, loser sets, points for each).',
      ruleSets: 'Points table line {line}: sets must be integers from 0 to 9.',
      ruleWinner:
        'Points table line {line}: the row is written from the winner, so their sets must be more.',
      rulePoints: 'Points table line {line}: points cannot exceed 99.',
      ruleRepeated: 'Points table line {line}: result {result} was already there.',
      groupName: 'Groups line {line}: the group name is missing.',
      groupId: 'Groups line {line}: “{name}” does not produce a valid id.',
      groupRepeated: 'Groups line {line}: “{name}” appears twice.',
      phaseId: 'Phase {phase}: the id is missing.',
      phaseIdInvalid:
        'Phase {phase}: the id only accepts letters, digits, dot, dash and underscore.',
      phaseName: 'Phase {id}: the name is missing.',
      phaseRepeated: 'Phase “{id}” appears twice.',
      phaseUnknownTeam: 'Phase {phase}: uses team “{id}”, which is not in the participants list.',
      tiebreakEmpty: 'At least one tiebreak criterion is required.',
    },
  },

  import: {
    title: 'Import history (.dvw)',
    navLabel: 'Import',
    intro:
      'Pick the .dvw files of a whole season: they are analysed first and nothing is stored until you hit Import. DataVolley files come in with rosters, line-ups, rallies and sanctions; whatever the format does not allow us to translate is listed below, line by line.',
    fileLabel: '.dvw files (you can select them all at once)',
    chosen: '{count} file(s) selected',
    notDvw: '{count} do not end in .dvw: they will be tried anyway.',
    noPermission: 'Your role cannot import history.',
    noPermissionHint:
      'The API requires the {permission} permission, held by the board and the statistician. The screen still shows in full so you know what to ask for.',
    noTeamsPermission: 'Matches will be imported, but the team database will not be touched.',
    noTeamsPermissionHint:
      'Filling in rosters requires {permission} (board). Without it the import loads the matches and leaves teams as they are, instead of doing half the job silently.',
    readingBatch: 'Analysing batch {batch} of {total}…',
    writingBatch: 'Importing batch {batch} of {total}…',
    previewCard: 'What will be imported',
    resultCard: 'What was imported',
    willImport: '{matches} new match(es) will be imported, with {events} events.',
    didImport: '{matches} match(es) imported, with {events} events.',
    alreadyImported: '{count} were already imported: untouched (the same file always yields the same match).',
    failedFiles: '{count} file(s) could not be read.',
    unmappedTotal: '{count} line(s) came in as a comment instead of an event: they are listed below.',
    teamsResult: 'Teams: {created} created, {merged} updated with new players.',
    importButton: 'Import {count} match(es)',
    nothingToImport: 'Nothing new to import.',
    previewOnly: 'Nothing has been written yet.',
    goToMatches: 'See the imported matches',
    unmappedCard: 'What could not be translated',
    unmappedIntro:
      'The importer discards nothing: whatever the canonical model does not represent comes in as a comment with its original line, so the file can be exported back without holes. This is exactly what ended up that way.',
    unmappedNone: 'The whole file was translated into events: no line was left over.',
    kind_unknown_code: 'Code the parser does not recognise (check the file)',
    kind_green_code: 'Point with no action detail (DataVolley green code)',
    kind_setter_change: 'Setter change (the model has no such event)',
    kind_sanction_no_team: 'Card with no team (refereeing crew)',
    teamsCard: 'Teams the history brings in',
    teamsNone: 'No file brings a roster.',
    teamState_created: 'will be created',
    teamState_merged: 'players will be added',
    teamState_unchanged: 'no changes',
    teamState_skipped: 'untouched (missing permission)',
    filesCard: 'File by file',
    colCode: 'Code',
    colWhy: 'Why',
    colLines: 'Lines',
    colFiles: 'Files',
    colSample: 'Sample',
    colTeam: 'Team',
    colNewPlayers: 'New players',
    colFile: 'File',
    colMatch: 'Match',
    colDate: 'Date',
    colSets: 'Sets',
    colEvents: 'Events',
    colUnmapped: 'Untranslated',
    colState: 'State',
    outcome_preview: 'ready to import',
    outcome_imported: 'imported',
    outcome_already_imported: 'already there',
    outcome_failed: 'could not be read',
  },

  teams: {
    title: 'Teams',
    newTeam: 'New team',
    editing: 'Editing {id}',
    loadingWhat: 'teams',
    empty:
      'The team database is empty. With «New team» a roster is loaded once and the scouting app reuses it in every match.',
    name: 'Name',
    namePlaceholder: 'Bolívar',
    teamId: 'Id (key in the archive)',
    teamIdPlaceholder: 'bolivar',
    code: 'Code (up to 8)',
    codePlaceholder: 'BOL',
    coach: 'Head coach',
    roster: 'Roster — one line per player: number, name and optional role ({roles})',
    rosterPlaceholder: '1 Pérez setter\n14 Palacios outside\n6 Gómez libero',
    liberos: 'Liberos (shirt numbers, comma separated)',
    liberosPlaceholder: '6, 12',
    savedNotice: 'Team «{name}» saved.',
    deletedNotice: 'Team «{name}» deleted.',
    confirmDelete: 'Delete team «{name}»? This cannot be undone.',
    colTeam: 'Team',
    colCode: 'Code',
    colCoach: 'Coach',
    colPlayers: 'Players',
    colLiberos: 'Liberos',
    colUpdated: 'Updated',
    colActions: 'Actions',
    edit: 'Edit',
    remove: 'Delete',
    roles: {
      setter: 'setter',
      outside: 'outside',
      opposite: 'opposite',
      middle: 'middle',
      libero: 'libero',
      unknown: 'no role',
    },
    errors: {
      idRequired: 'The team id is missing (it is the key in the archive).',
      idInvalid: 'The id only accepts letters, digits, dot, hyphen and underscore.',
      nameRequired: 'The team name is missing.',
      codeTooLong: 'The code cannot be longer than 8 characters.',
      badNumber: 'Line {line}: «{token}» is not a valid shirt number (0 to 99).',
      repeatedNumber: 'Line {line}: shirt #{number} is repeated.',
      badLibero: '«{token}» is not a valid libero shirt number.',
      liberoNotInRoster: 'Libero #{number} is not on the roster.',
    },
  },

  auth: {
    title: 'Sign in',
    intro: 'Sign in with your federation account to browse the archive with your role.',
    username: 'Username',
    usernamePlaceholder: 'first.last',
    password: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in…',
    logout: 'Sign out',
    loggingOut: 'Signing out…',
    missingFields: 'Enter both username and password.',
    badCredentials: 'Wrong username or password.',
    accountDisabled: 'This account is disabled: ask an administrator to re-enable it.',
    tooManyAttempts: 'Too many failed attempts. Wait a minute and try again.',
    sessionExpired: 'Your session expired or was revoked. Sign in again.',
    welcome: 'Signed in as {name}.',
    alreadyIn: 'You are already signed in as {name} ({role}).',
    goToMatches: 'Go to Matches',
    signedInAs: '{name} · {role}',
    since: 'Session started on {date}.',
    anon: 'Not signed in',
    anonTitle: 'Nobody signed in on this browser',
    login: 'Sign in',
    apiClosed:
      'The federation API requires credentials. Sign in with your account or paste the compatibility token in Settings.',
    compatCard: 'Compatibility mode',
    compatHint:
      "If you have the federation's single token instead of an account, paste it in Settings. It still works for integrations, but it leaves no trace of WHO did what: the audit log records it as «federation token».",
    compatLink: 'Go to Settings',
    viaSession: 'session',
    viaLegacyToken: 'compatibility token',
    viaOpen: 'open API (dev)',
    viaLegacyTokenTitle:
      'You are using the manual Bearer from Settings (compatibility mode): the API treats you as admin and the audit log cannot tell who you are.',
    viaOpenTitle: 'The API runs in dev open mode (SCOUT_DEV_OPEN=1): it asks for no credentials.',
    viaMatchCredential: 'match credential',
    viaMatchCredentialTitle:
      'You are in with the QR+PIN credential issued by the scorer’s table: it is valid for this match only and expires by itself.',
    roles: {
      admin: 'admin',
      scout: 'scout',
      coach: 'coach',
      viewer: 'board/press',
    },
  },

  users: {
    title: 'Users',
    navLabel: 'Users',
    intro:
      'The federation roster: who signs in, with which role and since when. No user cap and no per-seat pricing.',
    loadingWhat: 'the roster',
    empty: 'The roster is empty. Create the first user to stop sharing a single token.',
    newUser: 'New user',
    editing: 'Editing «{username}»',
    username: 'Username (lowercase, digits, dot, dash and underscore)',
    name: 'Full name',
    role: 'Role',
    passwordNew: 'Password (at least {min} characters)',
    passwordEdit: 'New password (empty = keep the current one)',
    passwordHint: 'Changing the password closes ALL open sessions of that person.',
    disabled: 'Account blocked (cannot sign in)',
    disabledHint: 'Blocking closes their sessions right away; the user and their history stay.',
    colUser: 'Username',
    colName: 'Name',
    colRole: 'Role',
    colStatus: 'Status',
    colCreated: 'Created',
    colActions: 'Actions',
    statusActive: 'Active',
    statusDisabled: 'Blocked',
    you: 'you',
    edit: 'Edit',
    resetPassword: 'Reset password',
    revoke: 'Close sessions',
    block: 'Block',
    unblock: 'Unblock',
    remove: 'Delete',
    working: '…',
    confirmDelete: 'Delete «{username}»? Their sessions are closed and the user cannot be restored.',
    confirmRevoke: 'Close every session of «{username}»? They will have to sign in again.',
    createdNotice: 'User «{username}» created.',
    savedNotice: 'User «{username}» saved.',
    deletedNotice: 'User «{username}» deleted.',
    revokedNotice: 'Sessions of «{username}» closed.',
    blockedNotice: '«{username}» blocked and their sessions closed.',
    unblockedNotice: '«{username}» unblocked.',
    revokeHint:
      'Closing sessions blocks and unblocks the account: it is all the API exposes today, so it leaves two audit entries.',
    revokeHalfway:
      'Sessions of «{username}» were closed, but the account IS STILL BLOCKED: unblock it from the list ({reason}).',
    selfDeleteTitle: 'You cannot delete your own user.',
    forbidden: 'Managing the roster is for the admin role only.',
    forbiddenHint:
      'Your role is «{role}». The roster is managed by whoever holds the «{permission}» permission: ask the federation for it.',
    anon: 'Sign in with an admin account to manage the roster.',
    roleHintAdmin: 'admin — everything, including the roster and the audit log.',
    roleHintScout: 'scout — enters the match and reads/exports everything.',
    roleHintCoach: 'coach — reads everything (including the .dvw), writes no events.',
    roleHintViewer: 'board/press — only the finished documents.',
    errors: {
      usernameRequired: 'The username is missing (it is the roster key and goes in the URL).',
      usernameInvalid: 'The username only accepts lowercase, digits, dot, dash and underscore.',
      nameRequired: "The person's name is missing.",
      passwordRequired: 'A password is mandatory when creating a user.',
      passwordShort: 'The password needs at least {min} characters.',
    },
  },

  settings: {
    title: 'Settings',
    connectionCard: 'API connection',
    apiUrl: 'API URL (empty = same origin as this portal)',
    token: 'Token (Bearer) — compatibility mode. Empty if you sign in with an account.',
    tokenHint:
      "It is the federation's single token (SCOUT_API_TOKEN), older than user accounts. It stays for integrations: if you signed in, your session wins and this field is ignored.",
    sessionCard: 'Session',
    sessionActive: 'You are signed in as {name} ({role}).',
    sessionNone: 'No session started on this browser.',
    sessionLogin: 'Sign in with an account',
    sessionLogout: 'Sign out',
    sessionUsingToken: 'This browser is using the compatibility token, not a session.',
    testConnection: 'Test connection',
    testing: 'Testing…',
    testOk: 'API v{version} · {count} match(es) reachable.',
    testBadToken: 'API v{version} answers, but rejected the token: check Authorization.',
    testPartial: 'API v{version} answers (the listing returned: {reason}).',
    savedHint: 'The test uses the SAVED configuration. If you changed something, save it first.',
    themeCard: 'Design',
    themeLabel: 'Choose the design',
    themeVar: 'New (VAR)',
    themeClasico: 'Classic',
    languageCard: 'Language',
    languageLabel: 'Interface language',
    languageHint: "Applied instantly and stored in this browser's localStorage.",
    efficiencyCard: 'Statistics convention',
    efficiencyIntro:
      'Every coaching staff computes efficiency and positivity with its own convention. The one chosen here rules ALL statistics tabs, the printable report and the season totals.',
    efficiencyLabel: 'Efficiency formulas',
    efficiencyHint:
      'Applied instantly, like the language. It never touches the log or any count: only which formula divides the evaluations already recorded.',
    replayCard: 'Replay server (VAR)',
    replayIntro:
      'This is THE VENUE video server, on the LAN. The clip goes straight from there to this browser, never through the API.',
    replayUrl: 'Replay server URL (empty = no VAR in this browser)',
    replayToken: 'Replay server token',
    replayHint:
      'If the portal is served over HTTPS and the VAR is plain HTTP on the LAN, the browser will block the request as mixed content: at the venue open the portal over HTTP, or publish the VAR with TLS.',
    howCard: 'How it works',
    howLocal: "Settings live in this browser's localStorage; they are not synced.",
    howLiveBefore: 'The live score is read with ',
    howLiveMiddle: ' in streaming mode (not with',
    howLiveAfter: ') precisely so the authorization header can be sent.',
    howCorsBefore: 'If the API is on another domain, it has to allow this origin via CORS (',
    howCorsAfter: ').',
  },

  api: {
    badRequest: 'The API rejected the request (400).',
    unauthorized: 'The API requires authentication: check the token in Settings.',
    forbidden: 'Your role is not allowed to do this (403). Changing the token in Settings will not help: the federation grants it.',
    forbiddenPermission:
      'Your role lacks the «{permission}» permission the API requires (403). The federation grants it; Settings cannot fix it.',
    notFound: 'The API did not find what was requested (404).',
    conflict: 'The match is incomplete (409).',
    unprocessable: "The match's rulebook is unknown (422).",
    notImplemented: 'The API cannot generate this yet (module pending).',
    serverError: 'Server error ({status}).',
    unexpected: 'Unexpected answer from the API ({status}).',
    noFetch: 'This environment has no fetch.',
    networkError: 'Could not reach the API ({reason}).',
    notJson: 'The API answered something that is not JSON.',
  },

  /** PUBLIC PORTAL (`/{slug}`): the federation's public face. */
  publicPortal: {
    title: 'Public portal',
    today: 'Today',
    todayHint: "Today's matches, live, without reloading the page.",
    todaySection: "Today's matches",
    upcoming: 'Upcoming matches',
    recent: 'Latest results',
    competitions: 'Competitions',
    results: 'Results',
    resultsHint: 'The full season archive, newest first.',
    competition: 'Competition',
    match: 'Match',
    fixture: 'Fixture',
    standings: 'Standings',
    evolution: 'Score progression',
    setsTable: 'Set scores',
    pointsByReason: 'How the points were won',
    reason: 'Reason',
    set: 'Set',
    total: 'Total',
    inPlay: 'in play',
    statusLive: 'Live',
    statusWalkover: 'W.O.',
    statusPostponed: 'Postponed',
    statusCancelled: 'Cancelled',
    matchday: 'Round {matchday}',
    tbd: 'TBD',
    seeAll: 'See all',
    teamsCount: '{count} teams',
    matchesCount: '{count} matches',
    filterCompetition: 'Competition:',
    allCompetitions: 'All',
    noDate: 'No date',
    noMatchesToday: 'No matches scheduled for today.',
    noCompetitions: 'No competitions published yet.',
    noResults: 'No results yet.',
    noFixture: 'The schedule for this competition has not been published yet.',
    noStandings: 'This competition has no standings yet.',
    noSets: 'The match has not started yet.',
    noPoints: 'No points have been played yet.',
    notStarted: 'The match is scheduled and has not started yet.',
    pendingMatches: '{count} matches of this phase are still to be played.',
    truncated: 'Showing the most recent results.',
    position: 'Pos',
    team: 'Team',
    points: 'Pts',
    played: 'P',
    won: 'W',
    lost: 'L',
    setsFor: 'Sets',
    setRatio: 'Set ratio',
    pointRatio: 'Point ratio',
    updated: 'Updated {ago}',
    agoSeconds: '{seconds}s ago',
    agoMinutes: '{minutes} min ago',
    retrying: 'retrying',
    stale: 'Could not refresh: you are seeing the last information received.',
    loadFailed: 'Could not load the information. It will retry on its own.',
    notFoundTitle: 'No portal here',
    notFoundBody: 'There is no public portal at /{slug}, or the federation has it switched off.',
    unknownRoute: 'This address does not exist in the portal.',
    staffLink: 'Federation login',
    widgetHint: 'For a broadcast:',
    widgetLink: 'scoreboard for OBS',
    widgetStale: 'Scoreboard not updating',
    widgetLabel: '{home} {setsHome} - {setsAway} {away}, current set {scoreHome} to {scoreAway}',
  },

  describe: {
    unknownPlayer: 'n/a',
    matchCreated: 'Match created: {name}',
    setStarted: 'Set {set} begins',
    setEnded: 'End of set {set}: {home}–{away} ({team})',
    matchEnded: 'End of match: {team} wins {setsHome}–{setsAway}',
    lineupSet: '{team} lineup: {rotation}',
    touch: '{team} · {skill} {evaluation} — {player}',
    touchZones: '{team} · {skill} {evaluation} — {player} ({startZone}→{endZone})',
    point: 'Point {team} off {reason} — {home}–{away}',
    substitution: 'Substitution in {team}: #{playerIn} in for #{playerOut}',
    substitutionLibero: 'Substitution in {team}: #{playerIn} in for #{playerOut} (libero)',
    timeoutTechnical: 'Technical timeout for {team}',
    timeoutRegular: 'Timeout for {team}',
    sanction: 'Sanction ({card}) to {team}',
    comment: 'Note: {text}',
    undo: 'Undo',
    correction: 'Correction',
    officialsSet: 'Officials recorded ({count})',
    toss: 'Toss: {team} wins and chooses {choice}',
    tossDecider: 'Deciding-set toss: {team} wins and chooses {choice}',
    lineupApproved: 'Line-up of {team} approved (set {set})',
    rallyStarted: '{team} serving · #{server}',
    challengeRequested: 'Challenge requested by {team}',
    challengeOfficial: 'Referee-initiated review',
    challengeResolved: 'Challenge resolved: {outcome} ({action})',
    replayBookmark: 'Video bookmark: {label}',
    signature: '{role} signature: {name}',
    sheetClosed: 'Scoresheet closed ({outcome})',
  },

  court: {
    net: 'NET',
    libero: 'libero',
    captain: 'captain',
    serving: 'Serving',
    playerTitle: '#{number} at P{position}',
    roles: {
      setter: 'S',
      outside: 'OH',
      opposite: 'OPP',
      middle: 'MB',
      libero: 'L',
    },
  },

  mesaPanel: {
    autoRefresh: 'Up to date',
    autoRefreshTitle: 'The scorer’s table refreshes itself every few seconds while the scoresheet is open.',
    scoreLabel: 'Match score',
    lineupsTitle: 'Line-ups on court · set {set}',
    courtLabel: 'Court: line-ups of {home} and {away}',
    flowLabel: 'Challenge flow',
    flow: {
      request: 'Request',
      reason: 'Reason',
      outcome: 'Outcome',
      score: 'Score',
      confirm: 'Record',
    },
    flowHint: {
      request: 'Bench buzzer or referee review',
      reason: 'The fault being challenged',
      outcome: 'One of the five VCS outcomes',
      score: 'What happens to the point',
      confirm: 'One batch to the scoresheet',
    },
    reviewTitle: 'Review',
    keeps: 'Keeps the challenge',
    loses: 'Loses the challenge',
    confirmNeeds: 'Pick the outcome and the score action to enable recording.',
    windowTitle: '{seconds} s window',
    windowOpen: '{seconds} s left to request a challenge',
    windowClosed: 'Closed: the last point can no longer be challenged',
    windowReview: 'Review in progress',
    windowNone: 'No point to challenge yet',
    windowHint: 'Art. 7 · counted from when this screen saw the point',
    qrLabel: 'QR code for {holder}’s credential',
    showQr: 'Show QR',
    hideQr: 'Hide QR',
    qrAgainHint: 'Scan it with the tablet camera: it opens the console with this role. The PIN is not shown again.',
    qrElsewhere: 'The QR can only be shown again on the laptop that issued the credential.',
  },

  benchPanel: {
    lineupHint: 'Set up the next set',
    state: {
      PENDING: 'Pending',
      ACCEPTED: 'Accepted',
      REJECTED: 'Rejected',
      CANCELLED: 'Cancelled',
    },
  },

  matchViews: {
    kpiPoints: 'Points ({byPlayer} attributed)',
    kpiSideout: 'Side-out',
    kpiBreak: 'Break',
    kpiAttack: 'Attack Eff%',
    kpiReception: 'Reception +',
    attacksCount: '{count} attacks',
    zoneSub: '{count} · Eff {eff}',
    setterCourtTitle: 'Distribution by attack zone',
    setterCourtAll: 'All',
    setterCourtGrade: 'R{grade}',
    setterCourtLabel: 'Court: {team} setter distribution — {what}',
    setterCourtOther:
      '{count} attacks without a 1–6 start zone (combinations without zone or uncoded): they are in the table, not on the court.',
    setterZoneTitle: 'Zone {zone}: {count} attacks ({pct}), efficiency {eff}',
    rotCourtSideout: 'Side-out by rotation',
    rotCourtBreak: 'Break by rotation',
    rotCourtLabel: 'Court: {what} for {team}',
    rotCourtHint:
      'Each zone is a rotation: R1 is the setter in zone 1 (serving), R2 in zone 2, and so on. Colour runs from the team’s worst to its best.',
    rotSub: '{won}/{total}',
    zonesCourtTitle: 'On the court',
    zonesCourtLabel: 'Court: {skill} map for {team}',
    zonesCourtHint:
      'Bottom, own court: where it started, coloured by efficiency. Top, opponent court: where it went, coloured by volume. Tap an own zone to see only what started there.',
    zonesCourtHintReception:
      'Bottom, own court: where it was received, coloured by efficiency. Top, opponent court: where the serve came from. Tap an own zone to see only those receptions.',
    zonesCount: '{count} touches',
    zonesOwnTitle: 'Zone {zone}: {count} touches, efficiency {eff}',
    zonesOtherTitle: 'Zone {zone}: {count} touches',
    zonesFiltering: 'Filtering zone {zone}',
    zonesClear: 'Show all',
    zonesNoFilter: 'All zones',
    zonesOther: '{count} touches without a 1–6 zone: they are in the matrix, not on the court.',
    zonesMatrixTitle: 'Start × end matrix',
  },
};
